import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Guid } from 'guid-typescript';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as _ from '..';
import { RemoveAffectedEntriesDialogComponent } from './remove-affected-entries-dialog/remove-affected-entries-dialog.component';


interface EditableEntry extends _.Entry {
  readonly entryType: EntryType;
  quantity: number;
}

enum EntryType {
  ProductRelatedEntry,
  CustomEntry
}

enum RelatedProductsState {
  AllUpToDate,
  AnyOutdated,
  AnyDeleted,
  AllDeleted
}

export enum ProductRelatedEntryState {
  ProductUpToDate,
  ProductOutdated,
  ProductDeleted
}

@Component({
  selector: 'app-entries-table',
  templateUrl: './entries-table.component.html',
  styleUrls: ['./entries-table.component.scss']
})
export class EntriesTableComponent extends _.EditableSectionPartComponent implements OnInit, OnDestroy, OnChanges {
  @Input() productsDataSource: ReadonlyArray<_.Product>;
  @Input() productFilter: (product: _.Product) => boolean;
  @Input() originalEntries: _.EntriesCollection;
  @Input() publishTotalCosts: boolean;
  @Input() ignoreCustomPositionsForStateDetermination: boolean;
  @Output() discardChangesCalled = new EventEmitter<_.EntriesCollection>();
  @Output() saveChangesCalled = new EventEmitter<_.EntriesCollection>();
  @Output() deleteCalled = new EventEmitter<_.EntriesCollection>();
  @Output() productFilterDiscarded = new EventEmitter();
  displayProductUpdates: boolean;
  dragDisabled: boolean = true;
  public products: ReadonlyArray<_.Product>;
  readonly getEntryTotalCosts = _.getEntryTotalCosts;
  readonly entries$: Observable<EditableEntry[]>;
  readonly MAX_CUSTOM_ENTRY_DESCRIPTION_LENGTH = _.MAX_CUSTOM_ENTRY_DESCRIPTION_LENGTH;
  readonly displayedColumns: string[] = ['productId', 'quantity', 'discountFactor', 'description', 'pricePerUnit', 'ap', 'totalCost', 'delete'];
  private readonly entriesSubject$: BehaviorSubject<EditableEntry[]>;
  private internalRelatedProductsState: RelatedProductsState;
  private internalTotalCosts = 0;

  public get entryTypes(): any {
    return EntryType;
  }

  public get relatedProductsState(): RelatedProductsState {
    return this.internalRelatedProductsState;
  }

  public get allProductsUpToDate(): boolean {
    return this.relatedProductsState === RelatedProductsState.AllUpToDate;
  }

  public get containsOutdatedProduct(): boolean {
    return this.relatedProductsState === RelatedProductsState.AnyOutdated;
  }

  public get containsDeletedProduct(): boolean {
    return this.relatedProductsState === RelatedProductsState.AnyDeleted;
  }

  public get containsOnlyDeletedProducts(): boolean {
    return this.relatedProductsState === RelatedProductsState.AllDeleted;
  }

  public get totalCosts(): number {
    const totalCosts = this.calculateTotalCosts(this.currentEntries, this.displayProductUpdates);
    // This is the only place where change of totalCosts can be detected.
    // The field internalCosts is used to avoid emitting the same value several times.
    if (this.publishTotalCosts && this.internalTotalCosts !== totalCosts) {
      this.totalCostsService.addOrUpdatePartialCosts(this.id, totalCosts);
      this.internalTotalCosts = totalCosts;
    }
    return totalCosts;
  }

  public get numberOfEntries(): number {
    return this.currentEntries.length;
  }

  private get currentEntries(): EditableEntry[] {
    return this.entriesSubject$.getValue();
  }

  constructor(
    private dialog: MatDialog,
    private totalCostsService: _.TotalCostsService,
    protected element: ElementRef<Element>,
    protected csBuildupStore: _.CsBuildupStateStore,
    protected csBuildupStateQuery: _.CsBuildupStateQuery) {
    super(element, csBuildupStore, csBuildupStateQuery);
    this.entriesSubject$ = new BehaviorSubject<EditableEntry[]>([]);
    this.entries$ = this.entriesSubject$.asObservable();
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.productsDataSource?.currentValue) {
      this.applyProductFilter()
      this.products = this.getFilteredProducts();
      // Clear the entries if the products are changed while the component is in edit mode
      if (!this.isReadonly) {
        this.entriesSubject$.next([]);
      }
      else {
        this.updateEditableEntries(this.originalEntries, this.products);
      }
    }

    if(changes.productFilter?.currentValue) {
      const filteredProducts = this.getFilteredProducts();
      const affectedEntries = this.getEntriesAffectedByTheProductFilter(filteredProducts);
      if (affectedEntries.length > 0) {
        this.dialog.open(RemoveAffectedEntriesDialogComponent, { data: { affectedEntries } })
          .afterClosed()
          .subscribe(removeAffectedEntriesConfirmed => removeAffectedEntriesConfirmed
            ? this.applyProductFilter(affectedEntries)
            : this.productFilterDiscarded.emit());
      } else {
        this.applyProductFilter();
        const entiriesCollection = createEntriesCollection(this.currentEntries);
        this.updateEditableEntries(entiriesCollection, this.products);
      };
    }

    if (changes.originalEntries?.currentValue) {
      this.updateEditableEntries(this.originalEntries, this.products);
    }
  }

  ngOnDestroy(): void {
    this.totalCostsService.removePartialCosts(this.id);
    super.ngOnDestroy();
  }

  onProductSelected(productRelatedEntry: EditableProductRelatedEntry, newProductId: number): void {
    productRelatedEntry.product = this.products.find(p => p.id === newProductId);
  }

  onDrop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.currentEntries, event.previousIndex, event.currentIndex);
    for (let i = 0; i < this.currentEntries.length; i++) {
      this.currentEntries[i].positionNumber = i;
    }
    
    this.entriesSubject$.next(this.currentEntries);
  }

  discardChanges(): void {
    this.discardChangesCalled.emit();
    this.updateEditableEntries(this.originalEntries, this.products);
    this.endEdit();
  }

  saveChanges(): void {
    this.originalEntries = createEntriesCollection(this.currentEntries);
    this.saveChangesCalled.emit(this.originalEntries);
    this.endEdit();
  }

  delete(): void {
    this.deleteCalled.emit();
  }

  addPrductRelatedEntry(): void {
    const newEntry = new EditableProductRelatedEntry(
      Guid.create(),
      new Date(),
      this.currentEntries.length + 1,
      this.products[0],
      1,
      0,
      false,
      this.products[0]
    );
    this.currentEntries.push(newEntry);
    this.entriesSubject$.next(this.currentEntries);
  }

  addCustomEntry(): void {
    const newEntry = new EditableCustomEntry(
      Guid.create(),
      new Date(),
      this.currentEntries.length + 1,
      'Benutzerdefinierte Position',
      0,
      1,
      0,
      false
    );
    this.currentEntries.push(newEntry);
    this.entriesSubject$.next(this.currentEntries);
  }

  removeEntry(entryToDelete: EditableEntry): void {
    const index = this.currentEntries.indexOf(entryToDelete, 0);
    if (index < 0) {
      return;
    }
    this.currentEntries.splice(index, 1);
    this.entriesSubject$.next(this.currentEntries);
  }

  alignEntriesToCurrentProducts(): void {
    const productRelatedEntries = this.getProductRelatedEntries(this.currentEntries);
    productRelatedEntries
      .filter(e => !e.latestProduct)
      .forEach(entryWithDeletedProduct => {
        const index = this.currentEntries.findIndex(e => e.id === entryWithDeletedProduct.id);
        this.currentEntries.splice(index, 1);
      });

    productRelatedEntries
      .filter(e => e.latestProduct && e.product.version < e.latestProduct.version)
      .forEach(entryWithOutdatedProduct => {
        entryWithOutdatedProduct.product = entryWithOutdatedProduct.latestProduct;
      });

    this.entriesSubject$.next(this.currentEntries);
    this.displayProductUpdates = false;
    this.saveChanges();
  }

  isAlreadyPartOfSelection(productId: number): boolean {
    return this.getProductRelatedEntries(this.currentEntries)
      .some(e => e.product.id === productId);
  }

  private updateEditableEntries(source: _.EntriesCollection, products: ReadonlyArray<_.Product>): void {
    const editableEntries = !source ? [] : createEditableEntries(source.productRelatedEntries, source.customEntries, products);
    this.entriesSubject$.next(editableEntries);

    const ignoreCustomEntries =
      this.ignoreCustomPositionsForStateDetermination ||
      editableEntries.every(e => e.entryType === EntryType.ProductRelatedEntry);
    const productRelatedEntries = this.getProductRelatedEntries(editableEntries);
    this.internalRelatedProductsState = this.getRelatedProductsState(ignoreCustomEntries, productRelatedEntries);
  }

  private getRelatedProductsState(
    ignoreCustomEntries: boolean,
    entries: EditableProductRelatedEntry[])
    : RelatedProductsState {
    return ignoreCustomEntries && entries.every(e => e.relatedProductWasDeleted)
      ? RelatedProductsState.AllDeleted
      : entries.some(e => e.relatedProductWasDeleted)
        ? RelatedProductsState.AnyDeleted
        : entries.some(e => e.product.version < e.latestProduct.version)
          ? RelatedProductsState.AnyOutdated
          : RelatedProductsState.AllUpToDate;
  }

  private calculateTotalCosts(entries: EditableEntry[], useLatestPrice: boolean): number {
    if (this.displayProductUpdates && this.relatedProductsState === RelatedProductsState.AllDeleted) {
      return 0;
    }

    const customEntriesTotalCosts = this.getCustomEntries(entries)
      .reduce((a, u) => a + this.getEntryTotalCosts(u, u.quantity), 0);
    const productRelatedEntries = this.getProductRelatedEntries(entries)
      .reduce((a, u) => a + u.getTotalCosts(useLatestPrice, u.quantity), 0);
    return customEntriesTotalCosts + productRelatedEntries;
  }

  private getProductRelatedEntries(entries: EditableEntry[]): EditableProductRelatedEntry[] {
    return entries
      .filter(e => e.entryType === EntryType.ProductRelatedEntry)
      .map(e => e as EditableProductRelatedEntry);
  }

  private getCustomEntries(entries: EditableEntry[]): EditableCustomEntry[] {
    return entries
      .filter(e => e.entryType === EntryType.CustomEntry)
      .map(e => e as EditableCustomEntry);
  }

  private getFilteredProducts(): ReadonlyArray<_.Product> {
    if(!this.productsDataSource) return [];
    if(!this.productFilter) return this.productsDataSource; 
    return this.productsDataSource.filter(this.productFilter);
  }

  private getEntriesAffectedByTheProductFilter(products: ReadonlyArray<_.Product>) {
    return this.getProductRelatedEntries(this.currentEntries)
      .filter(e => products.every(p => p.id != e.product.id));
  }

  private applyProductFilter(affectedEntries?: EditableProductRelatedEntry[]) {
    affectedEntries?.forEach(e => this.removeEntry(e));
    this.products = this.getFilteredProducts();
  }
}

function createEntriesCollection(editableEntries: EditableEntry[]): _.EntriesCollection {
  const productRelatedEntries = editableEntries
    .filter(e => e.entryType === EntryType.ProductRelatedEntry)
    .map(entry => entry as EditableProductRelatedEntry)
    .map(entry => ({
      ...entry,
      productId: entry.product.id,
      productVersion: entry.product.version,
      description: entry.product.name,
      pricePerUnit: entry.product.pricePerUnit
    }));
  const customEntries = editableEntries
  .filter(e => e.entryType === EntryType.CustomEntry)
  .map(entry => ({...entry as EditableCustomEntry}));
  return {
    productRelatedEntries,
    customEntries
  }
}

function createEditableEntries(
  productEntries: _.ProductRelatedEntry[],
  customEntries: _.CustomEntry[],
  latestProducts: ReadonlyArray<_.Product>): EditableEntry[]{
    const editableProductRelatedEntries = createEditableProductRelatedEntries(productEntries, latestProducts);
    const editableCustomEntries = createEditableCustomEntries(customEntries);
    return editableProductRelatedEntries
      .concat(editableCustomEntries)
      .sort((a, b) => a.positionNumber - b.positionNumber);
}

function createEditableProductRelatedEntries(productRelatedEntries: _.ProductRelatedEntry[], latestProducts: ReadonlyArray<_.Product>)
  : EditableEntry[]
{
  const editableProductRelatedEntries: EditableProductRelatedEntry[] = [];
  productRelatedEntries.forEach(entry => {
    const product = _.createProduct(entry);
    const latestProduct = latestProducts
      ? findProduct(entry.productId, latestProducts)
      : undefined;
    editableProductRelatedEntries.push(
      new EditableProductRelatedEntry(
        entry.id,
        entry.createdOn,
        entry.positionNumber,
        product,
        entry.quantity,
        entry.discountFactor,
        entry.isAlternative,
        latestProduct));
  });
  return editableProductRelatedEntries;
}

function createEditableCustomEntries(customEntries: _.CustomEntry[]): EditableEntry[] {
  const editableCustomEntries: EditableCustomEntry[] = [];
  customEntries.forEach(entry => {
    editableCustomEntries.push(EditableCustomEntry.createFromCustomEntry(entry));
  });
  return editableCustomEntries;
}

function findProduct(productId: number, products: ReadonlyArray<_.Product>): _.Product {
  const index = products.findIndex(p => p.id === productId);
  return index > -1
    ? products[index]
    : undefined;
}

class EditableProductRelatedEntry implements EditableEntry {
  readonly id: Guid;
  readonly createdOn: Date;
  readonly entryType: EntryType;
  readonly latestProduct: _.Product;
  product: _.Product;
  quantity: number;
  discountFactor: number;
  isAlternative: boolean;
  positionNumber: number;

  public get relatedProductWasDeleted(): boolean {
    return this.state === ProductRelatedEntryState.ProductDeleted;
  }

  public get relatedProductWasUpdated(): boolean {
    return this.state === ProductRelatedEntryState.ProductOutdated;
  }

  public get pricePerUnit(): number {
    return this.product.pricePerUnit;
  }

  public get state(): ProductRelatedEntryState {
    return !this.latestProduct
      ? ProductRelatedEntryState.ProductDeleted
      : this.latestProduct.version > this.product.version
        ? ProductRelatedEntryState.ProductOutdated
        : ProductRelatedEntryState.ProductUpToDate;
  }

  constructor(
    id: Guid,
    createdOn: Date,
    positionNumber: number,
    product: _.Product,
    quantity: number,
    discountFactor: number,
    isAlternative: boolean,
    latestProduct: _.Product) {
    this.id = id;
    this.createdOn = createdOn;
    this.positionNumber = positionNumber;
    this.entryType = EntryType.ProductRelatedEntry;
    this.product = product;
    this.quantity = quantity;
    this.discountFactor = discountFactor;
    this.isAlternative = isAlternative;
    this.latestProduct = latestProduct;
  }

  getPricePerUnit(useLatestPrice: boolean): number {
    return useLatestPrice && this.state === ProductRelatedEntryState.ProductOutdated
      ? this.latestProduct.pricePerUnit
      : this.product.pricePerUnit;
  }

  getTotalCosts(useLatestPrice: boolean, quantity: number): number {
    const priceInfo = this.getPriceInfo(useLatestPrice);
    return _.getEntryTotalCosts(priceInfo, quantity);
  }

  private getPriceInfo(useLatestPrice: boolean): _.EntyPriceInfo {
    const pricePerUnit = useLatestPrice
      ? this.state === ProductRelatedEntryState.ProductDeleted
        ? 0
        : this.latestProduct.pricePerUnit
      : this.product.pricePerUnit;
    return {
      pricePerUnit,
      isAlternative: this.isAlternative,
      discountFactor: this.discountFactor };
  }
}

class EditableCustomEntry implements EditableEntry {
  readonly id: Guid;
  readonly createdOn: Date;
  readonly entryType: EntryType;
  description: string;
  pricePerUnit: number;
  quantity: number;
  discountFactor: number;
  isAlternative: boolean;
  positionNumber: number;

  constructor(id: Guid,
              createdOn: Date,
              positionNumber: number,
              description: string,
              pricePerUnit: number,
              quantity: number,
              discountFactor: number,
              isAlternative: boolean) {
    this.id = id;
    this.createdOn = createdOn;
    this.positionNumber = positionNumber;
    this.entryType = EntryType.CustomEntry;
    this.description = description;
    this.pricePerUnit = pricePerUnit;
    this.quantity = quantity;
    this.discountFactor = discountFactor;
    this.isAlternative = isAlternative;
  }

  static createFromCustomEntry(customEntry: _.CustomEntry): EditableCustomEntry {
    return new EditableCustomEntry(
      customEntry.id,
      customEntry.createdOn,
      customEntry.positionNumber,
      customEntry.description,
      customEntry.pricePerUnit,
      customEntry.quantity,
      customEntry.discountFactor,
      customEntry.isAlternative
    );
  }
}
